import React, { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import useDocHistory from 'hooks/useDocHistory';
import Callout from 'components/callout/callout';
import DateTime from 'components/text/DateTime';
import Tag from 'components/tags/tag';
import { HistoryEntryType } from 'types';
import DocList, { ViewMode } from 'components/list/docList/docList';
import TableList from 'components/list/tableList/tableList';
import TableListRow from 'components/list/tableList/tableListRow';
import TableListCell from 'components/list/tableList/tableListCell';
import HistoryFilter from './HistoryFilter';
import UserCard from '../user/UserCard';

interface HistoryListProps {
    storageId: string;
    categoryId?: string;
    locationId?: string;
    customerId?: string;
    supplierId?: string;
    productId?: string;
    transactionId?: string;
    actionId?: string;
    displayDocType?: boolean;
    allDocTypes?: boolean;
}

const HistoryList: React.FC<HistoryListProps> = ({
    storageId,
    categoryId,
    locationId,
    customerId,
    supplierId,
    productId,
    transactionId,
    actionId,
    displayDocType = false,
    allDocTypes = false
}) => {
    const { t } = useTranslation();
    const { historyEntries, docType, isLoading, error } = useDocHistory({
        storageId,
        categoryId,
        locationId,
        customerId,
        supplierId,
        productId,
        transactionId,
        actionId,
        allDocTypes
    });

    // Filter state
    const [filterDocumentType, setFilterDocumentType] = useState<string>('');
    const [filterDocumentId, setFilterDocumentId] = useState<string>('');
    const [filterActionType, setFilterActionType] = useState<string>('');

    // Apply filters
    const filteredHistoryEntries = useMemo(() => {
        let filtered = [...historyEntries];

        // Filter by document type
        if (filterDocumentType) {
            filtered = filtered.filter(entry => 
                (entry.docType || docType) === filterDocumentType
            );
        }

        // Filter by document ID
        if (filterDocumentId) {
            const needle = filterDocumentId.toLowerCase();
            filtered = filtered.filter(entry =>
                Object.values(entry.ids || {}).some(id =>
                    String(id).toLowerCase().includes(needle)
                )
            );
        }

        // Filter by action type
        if (filterActionType) {
            filtered = filtered.filter(entry => entry.type === filterActionType);
        }

        return filtered;
    }, [historyEntries, filterDocumentType, filterDocumentId, filterActionType, docType]);

    const getActionTypeColor = (type: HistoryEntryType): 'green' | 'blue' | 'red' | 'yellow' | 'gray' => {
        switch (type) {
            case HistoryEntryType.CREATE:
                return 'green';
            case HistoryEntryType.MODIFY:
                return 'blue';
            case HistoryEntryType.DELETE:
                return 'red';
            case HistoryEntryType.RESTORE:
                return 'yellow';
            case HistoryEntryType.DRAFT:
                return 'gray';
            default:
                return 'blue';
        }
    };

    const getActionTypeLabel = (type: HistoryEntryType): string => {
        switch (type) {
            case HistoryEntryType.CREATE:
                return t('historyActionCreate');
            case HistoryEntryType.MODIFY:
                return t('historyActionModify');
            case HistoryEntryType.DELETE:
                return t('historyActionDelete');
            case HistoryEntryType.RESTORE:
                return t('historyActionRestore');
            case HistoryEntryType.DRAFT:
                return t('historyActionDraft');
            default:
                return type;
        }
    };

    const getHistoryActionLabel = (type: HistoryEntryType, entryDocType?: string): string => {
        const actionLabel = getActionTypeLabel(type);
        if (!displayDocType) {
            return actionLabel;
        }
        return `${actionLabel} ${getDocTypeLabel(entryDocType || docType)}`;
    };

    const getDocTypeLabel = (type: string): string => {
        switch (type) {
            case 'storage':
                return t('storage');
            case 'category':
                return t('category');
            case 'location':
                return t('location');
            case 'product':
                return t('product');
            case 'customer':
                return t('customer');
            case 'supplier':
                return t('supplier');
            case 'transaction':
                return t('transaction');
            case 'action':
                return t('action');
            default:
                return type;
        }
    };

    const getDocumentUrl = (entry: any): string | undefined => {
        const entryDocType = entry.docType || docType;
        const entryIds = entry.ids || {};

        switch (entryDocType) {
            case 'storage':
                return `/storage`;
            case 'category':
                return entryIds.categoryId ? `/storage/categories/${entryIds.categoryId}` : undefined;
            case 'location':
                return entryIds.locationId ? `/storage/locations/${entryIds.locationId}` : undefined;
            case 'product':
                return entryIds.productId ? `/storage/products/${entryIds.productId}` : undefined;
            case 'customer':
                return entryIds.customerId ? `/storage/customers/${entryIds.customerId}` : undefined;
            case 'supplier':
                return entryIds.supplierId ? `/storage/suppliers/${entryIds.supplierId}` : undefined;
            case 'transaction':
                return entryIds.transactionId ? `/storage/transactions/${entryIds.transactionId}` : undefined;
            case 'action':
                // Actions need parent product ID
                return entryIds.productId ? `/storage/products/${entryIds.productId}` : undefined;
            default:
                return undefined;
        }
    };

    if (error) {
        return (
            <Callout type="error">
                {t('errorLoadingHistory')}
            </Callout>
        );
    }

    return (
        <DocList
            id="history_list"
            viewModes={[ViewMode.Table]}
            defaultViewMode={ViewMode.Table}
            showResults={isLoading || filteredHistoryEntries.length > 0}
            noResultsElement={
                <Callout type="info">
                    {(filterDocumentType || filterDocumentId || filterActionType)
                        ? t('noHistoryMatchingFiltersDescription')
                        : t('noHistoryDescription')}
                </Callout>
            }
            filterElement={
                <HistoryFilter
                    filterDocumentType={filterDocumentType}
                    setFilterDocumentType={setFilterDocumentType}
                    filterDocumentId={filterDocumentId}
                    setFilterDocumentId={setFilterDocumentId}
                    filterActionType={filterActionType}
                    setFilterActionType={setFilterActionType}
                    isLoading={isLoading}
                />
            }
        >
            <div data-view-mode={ViewMode.Table}>
                <TableList>
                    <TableListRow isHeader={true}>
                        <TableListCell>{t('columnUser')}</TableListCell>
                        <TableListCell>{t('columnAction')}</TableListCell>
                        <TableListCell>{t('columnDate')}</TableListCell>
                    </TableListRow>
                    {filteredHistoryEntries.map((entry, index) => {
                        const docUrl = getDocumentUrl(entry);
                        
                        return (
                            <TableListRow key={index} to={docUrl}>
                                <TableListCell>
                                    <div className="flex items-center gap-2">
                                        <UserCard userId={entry.userId} size="smallest" />
                                    </div>
                                </TableListCell>
                                <TableListCell>
                                    <Tag colorScheme={getActionTypeColor(entry.type)}>
                                        {getHistoryActionLabel(entry.type, entry.docType)}
                                    </Tag>
                                </TableListCell>
                                <TableListCell>
                                    <DateTime timestamp={entry.timestamp} hideTimeOnSm hideTimeOnMd />
                                </TableListCell>
                            </TableListRow>
                        );
                    })}
                    { isLoading && (
                        Array.from({ length: 3 }).map((_, _index) => (
                            <TableListRow key={_index}>
                                <TableListCell><p className="skeleton"></p></TableListCell>
                                <TableListCell><p className="skeleton"></p></TableListCell>
                                <TableListCell><p className="skeleton"></p></TableListCell>
                            </TableListRow>
                        ))
                    )}
                </TableList>
            </div>
        </DocList>
    );
};

export default HistoryList;
